import moment from "moment";
import { apiGet } from "../../Utils/apiFetch";
import apiPath from "../../Utils/apiPath";
import { useEffect, useRef, useState } from "react";
import { useContextSelector } from "use-context-selector";
import AuthContext from "../../context/AuthContext";
import { toFixedWithoutRounding } from "../../Utils/Toast";
import BettingSettledDetail from "./BettingSettledDetail";
import { isEmpty } from "lodash";
import NoData from "../../component/NoData";
import { t } from "i18next";

const BettingSettled = ({ searchParams, show }) => {
  const profileData = useContextSelector(AuthContext, (ctx) => {
    return ctx.profileData;
  });

  let gameCategories = {
    Casino: "casino",
    BetFair: "betfair",
    Bookmaker: "bookmaker",
    SportsBook: "sportBook",
    Fancy: "fancy",
  };
  const [data, setData] = useState([]);
  const [single, setSingle] = useState({});
  const [filter, setFilter] = useState(() => {
    const betType = !isEmpty(searchParams)
      ? gameCategories[searchParams?.gameType]
      : "betfair";

    return {
      betType,
      status: "completed",
      fromPeriod:
        moment(searchParams?.fromPeriod).format("YYYY-MM-DD") ||
        moment().subtract(1, "days").format("YYYY-MM-DD"),
      toPeriod:
        moment(searchParams?.toPeriod).format("YYYY-MM-DD") ||
        moment().format("YYYY-MM-DD"),
      page: 1,
      limit: 50,
    };
  });
  const groupByUpdatedDateWithBets = (arr) => {
    // Filter only markets having bets_list > 0
    const filtered = arr.filter((item) => item?.bets_list?.length > 0);

    // Sort by updatedAt descending (latest first)
    const sorted = filtered.sort(
      (a, b) => new Date(b.updatedAt) - new Date(a.updatedAt),
    );

    // Group by date
    let grouped = sorted.reduce((acc, item) => {
      const date = new Date(item.updatedAt).toISOString().split("T")[0]; // YYYY-MM-DD

      if (!acc[date]) {
        acc[date] = [];
      }

      acc[date].push(item);
      return acc;
    }, {});

    return Object.entries(grouped).map(([date, markets]) => ({
      date,
      markets,
    }));
  };

  const calculateTotal = (markets, newTab) => {
    if (!Array.isArray(markets) || markets.length === 0) return 0;

    const commissionRate = parseFloat(profileData?.commission) || 0;

    const total = markets.reduce((marketAcc, market) => {
      let backProfit = 0,
        backLoss = 0,
        layProfit = 0,
        layLoss = 0,
        yesProfit = 0,
        yesLoss = 0,
        noProfit = 0,
        noLoss = 0;

      // ✅ Process each bet & add updatedResult
      market.bets_list = market?.bets_list?.map((bet) => {
        const profit = parseFloat(bet.profitAmount) || 0;
        const loss = parseFloat(bet.loseAmount) || 0;

        let updateResultValue = 0;

        // 🎯 Back / Lay Bets (Exchange)
        if (bet?.betType === "back") {
          if (bet?.teamSelectionWin === bet?.selectionId) {
            backProfit += profit;
            updateResultValue = profit; // Win
          } else {
            backLoss += loss;
            updateResultValue = -loss; // Loss
          }
        } else if (bet?.betType === "lay") {
          if (bet?.teamSelectionWin !== bet?.selectionId) {
            layProfit += profit;
            updateResultValue = profit; // Win
          } else {
            layLoss += loss;
            updateResultValue = -loss; // Loss
          }
        }

        // 🎯 Fancy Yes / No Bets
        if (bet?.type === "Yes") {
          if (bet?.decisionRun >= bet?.betRun) {
            yesProfit += profit;
            updateResultValue = profit; // Win
          } else {
            yesLoss += loss;
            updateResultValue = -loss; // Loss
          }
        } else if (bet?.type === "No") {
          if (bet?.decisionRun < bet?.betRun) {
            noProfit += profit;
            updateResultValue = profit; // Win
          } else {
            noLoss += loss;
            updateResultValue = -loss; // Loss
          }
        }

        // ✅ Add single key for result
        return {
          ...bet,
          updatedResult: updateResultValue,
        };
      });

      // 🎯 Calculate subtotals
      const backSubTotal = backProfit - backLoss;
      const laySubTotal = layProfit - layLoss;
      const marketSubTotal = backSubTotal + laySubTotal;

      const yesSubTotal = yesProfit - yesLoss;
      const noSubTotal = noProfit - noLoss;
      const fancySubTotal = yesSubTotal + noSubTotal;

      // 🎯 Calculate Net Amount based on Tab
      let netAmount = 0;
      if (newTab === "toss" || newTab === "tie") {
        netAmount = marketSubTotal;
      } else if (newTab === "fancy") {
        netAmount = fancySubTotal;
      } else {
        const commission =
          marketSubTotal > 0 ? (marketSubTotal * commissionRate) / 100 : 0;
        netAmount = marketSubTotal - commission;
      }

      return marketAcc + netAmount;
    }, 0);

    return total;
  };
  const TotalLength = (markets) => {
    if (!Array.isArray(markets) || markets.length === 0) return 0;
    return markets.reduce((total, match) => {
      return total + match.bets_list.length;
    }, 0);
  };

  const calculateTotalAmount = (markets) => {
    if (!Array.isArray(markets) || markets.length === 0) return 0;
    return markets.reduce((total, match) => {
      const matchTotal = match.bets_list.reduce(
        (sum, bet) => sum + Number(bet.amount || 0),
        0,
      );
      return total + matchTotal;
    }, 0);
  };
  const groupByPlatformAndDate = (data) => {
    const grouped = data.reduce((acc, item) => {
      const platform = item.platform || "Unknown";
      const date =
        item.createdAt || new Date(item.createdAt).toISOString().split("T")[0];
      const key = `${platform}_${date}`; // unique combo

      if (!acc[key]) {
        acc[key] = {
          platform,
          date,
          totalBets: 0,
          totalBetAmount: 0,
          totalProfitAmount: 0,
          totalLoseAmount: 0,
          total: 0,
          items: [],
        };
      }

      acc[key].totalBets += 1;
      acc[key].totalBetAmount += Number(item.betAmount) || 0;
      acc[key].totalProfitAmount += Number(item.profitAmount) || 0;
      acc[key].totalLoseAmount += Number(item.loseAmount) || 0;
      acc[key].total += Number(item.playerPL) || 0;
      acc[key].items.push(item);

      return acc;
    }, {});

    // Convert object to sorted array (latest date first)
    return Object.values(grouped).sort(
      (a, b) => new Date(b.date) - new Date(a.date),
    );
  };

  const getData = async () => {
    const { status, data } = await apiGet(apiPath.profitLoss, filter);
    if (status == 200) {
      if (data?.success) {
        setData(
          data?.results?.data?.length > 0
            ? filter?.betType != "casino"
              ? groupByUpdatedDateWithBets(data?.results?.data)?.map((res) => {
                  return {
                    ...res,
                    total: calculateTotal(res?.markets, filter?.betType),
                    totalBetAmount: calculateTotalAmount(res?.markets),
                  };
                })
              : data?.results?.data
            : [],
        );
      }
    }
  };
  const pendingFetch = useRef(false); //
  const isFirstRender = useRef(true);
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    if (!searchParams || Object.keys(searchParams).length === 0) return;

    const newBetType = searchParams?.gameType
      ? (gameCategories[searchParams.gameType] ?? "betfair")
      : "betfair";

    const fromPeriod = searchParams?.fromPeriod
      ? moment(searchParams.fromPeriod).format("YYYY-MM-DD")
      : moment().subtract(1, "days").format("YYYY-MM-DD");

    const toPeriod = searchParams?.toPeriod
      ? moment(searchParams.toPeriod).format("YYYY-MM-DD")
      : moment().format("YYYY-MM-DD");

    // ✅ Update filter state
    setFilter((prev) => ({
      ...prev,
      betType: newBetType,
      fromPeriod,
      toPeriod,
    }));

    pendingFetch.current = true; // ✅ Mark that a fetch is needed
  }, [searchParams]);

  useEffect(() => {
    if (!show && pendingFetch.current) {
      pendingFetch.current = false;
      getData();
    }
  }, [show]);

  // ✅ Remove filter from getData's useEffect dependency
  // Keep this only for initial load
  useEffect(() => {
    getData(); // initial fetch only
  }, []);
  console.log(data, "data");
  return data?.length > 0 ? (
    <>
      {isEmpty(single) && (
        <>
          <div
            _ngcontent-serverapp-c3068904919
            className="list-title ng-tns-c3068904919-124"
          >
            <div
              _ngcontent-serverapp-c3068904919
              className="list-field platform ng-tns-c3068904919-124"
            >
              {t("Platform")}
            </div>
            <div
              _ngcontent-serverapp-c3068904919
              className="list-field type ng-tns-c3068904919-124"
            >
              Total Bets
            </div>
            <div
              _ngcontent-serverapp-c3068904919
              className="list-field bet ng-tns-c3068904919-124"
            >
              Stake
            </div>
            <div
              _ngcontent-serverapp-c3068904919
              className="list-field profit ng-tns-c3068904919-124"
            >
              {t("ProfitLoss")}
            </div>
          </div>
          {data?.map((res) => {
            let total = toFixedWithoutRounding(Math.abs(res?.total));
            let totalBet = TotalLength(res?.markets);

            return (
              <div
                _ngcontent-serverapp-c3068904919
                className="list-group ng-tns-c3068904919-58 ng-trigger ng-trigger-staggerFadeAnimation ng-star-inserted"
                style={{}}
              >
                <div
                  _ngcontent-serverapp-c3068904919
                  className="date-title ng-tns-c3068904919-58"
                >
                  <div
                    _ngcontent-serverapp-c3068904919
                    className="date ng-tns-c3068904919-58"
                  >
                    <i
                      _ngcontent-serverapp-c3068904919
                      className="ng-tns-c3068904919-58"
                      style={{
                        "mask-image":
                          'url("/assets/Other/icon-calendar-type02.svg")',
                      }}
                    />
                    {moment(res?.date).format("DD-MM-YYYY")}
                  </div>
                  <div
                    _ngcontent-serverapp-c3068904919
                    className="time-zone ng-tns-c3068904919-58"
                  >
                    GMT+5.5
                  </div>
                </div>
                <ul
                  _ngcontent-serverapp-c3068904919
                  className="list-content ng-tns-c3068904919-58"
                >
                  <li
                    onClick={() => setSingle(res)}
                    _ngcontent-serverapp-c3068904919
                    className="ng-tns-c3068904919-58 ng-star-inserted"
                  >
                    <div
                      _ngcontent-serverapp-c3068904919
                      className="list-field platform ng-tns-c3068904919-58"
                    >
                      {filter?.betType == "casino" ? "Casino" : "Exchange"}
                    </div>
                    <div
                      _ngcontent-serverapp-c3068904919
                      className="list-field type ng-tns-c3068904919-58"
                    >
                      {filter?.betType == "casino"
                        ? res?.platform
                        : `${totalBet} Bets`}
                    </div>
                    <div
                      _ngcontent-serverapp-c3068904919
                      className="list-field bet ng-tns-c3068904919-58"
                    >
                      <i
                        id="locale-util-dicrective-6"
                        className="ng-tns-c3068904919-58"
                        style={{ display: "initial" }}
                      >
                        {res?.totalBetAmount || res?.betAmount}
                      </i>
                    </div>
                    <div
                      _ngcontent-serverapp-c3068904919
                      className={`list-field ${
                        (
                          filter?.betType == "casino"
                            ? res?.playerPL > 0
                            : res?.total > 0
                        )
                          ? "positive"
                          : "negative"
                      } profit ng-tns-c3068904919-58`}
                    >
                      <i
                        id="locale-util-dicrective-7"
                        className="ng-tns-c3068904919-58"
                        style={{ display: "initial" }}
                      >
                        {filter?.betType == "casino"
                          ? toFixedWithoutRounding(Math.abs(res?.playerPL))
                          : res?.total > 0
                            ? toFixedWithoutRounding(Math.abs(total))
                            : `(${toFixedWithoutRounding(Math.abs(total))})`}
                      </i>
                    </div>
                    <div
                      _ngcontent-serverapp-c3068904919
                      className="list-arrow ng-tns-c3068904919-58"
                      style={{
                        "mask-image":
                          'url("/assets/Other/icon-arrow-type01.svg")',
                      }}
                    />
                  </li>
                </ul>
              </div>
            );
          })}{" "}
        </>
      )}
      {!isEmpty(single) && (
        <BettingSettledDetail
          data={single}
          setSingle={setSingle}
          betType={filter?.betType}
        />
      )}
    </>
  ) : (
    <NoData />
  );
};

export default BettingSettled;
